// How the frontend reads the recruiter offer contract.
//
// Contract source: work/pricing-consolidation/frontend-contract.md, verified
// against backend/myapp/serializers.py (PlanSerializer.recruiterOffer) and
// backend/myapp/recruiter_entitlements.py (offer_payload / quota_payload).
//
// Three rules the whole file exists to enforce.
//
// **Money comes from the server, in minor units.** `recruiterOffer.price.amountMinor`
// is the exact charge. `PlanPrice.price` is a whole-unit integer that rounds
// $950.40 down to $950, and the mobile backend mirrors that column, so it is
// never used for display or for checkout. Nothing here multiplies an interview
// count by a rate: the old $1-per-interview arithmetic cannot express a 20%
// annual discount and is gone.
//
// **`null` and `"unlimited"` are opposites.** A cap of `"unlimited"` means no
// ceiling. A cap of `null` means the operator never configured it and additions
// are DENIED server side. Reading `null` as unlimited is the one mistake in this
// contract that would show a customer capacity they do not have, so `readCap`
// keeps the two apart and no caller gets a bare truthiness check.
//
// **Only `visibility: "public"` is purchasable.** The backend already filters the
// catalogue, and this module filters again on the way in. The hidden Scale offer
// must never reach a card or a checkout body even if a response carries it.

/** Offer codes the public catalogue is allowed to show. */
export const PUBLIC_OFFER_CODES = ["free", "recruiter_99"];

/** Free's plan row id. Plan 1 is shared with candidate practice accounts. */
export const FREE_PLAN_ID = 1;

/** One interview, up to this many seconds. Supporting text, never the headline. */
export const MAX_INTERVIEW_SECONDS = 900;

// What the public page advertises, in minor units, per offer and billing cycle.
// This is a SAFETY RAIL, not the display source: the card renders the server's
// `amountMinor`, and checkout is refused when the server's number is not the one
// below. A recruiter is therefore never charged an amount this page did not show.
//
// recruiter_99 annual is 12 x 9900 = 118800 minor, less 20% = 95040 minor.
// `annualMonthlyEquivalentMinor` below recomputes that rather than restating it.
export const ADVERTISED_AMOUNT_MINOR = {
    free: { 1: 0, 12: 0 },
    recruiter_99: { 1: 9900, 12: 95040 },
};

/** Provisional annual discount on the monthly amount. */
export const ANNUAL_DISCOUNT_RATE = 0.2;

/** Months in each billing cycle. */
export const CYCLE_MONTHS = { monthly: 1, yearly: 12 };

const CURRENCY_SYMBOLS = { USD: "$", INR: "₹" };

/**
 * Exact annual amount for a monthly amount, in minor units.
 * Rounded to the nearest minor unit so it is always a whole number of cents.
 */
export function annualAmountMinor(monthlyMinor, discountRate = ANNUAL_DISCOUNT_RATE) {
    return Math.round(monthlyMinor * 12 * (1 - discountRate));
}

/** What an annual amount works out to per month, in minor units. */
export function annualMonthlyEquivalentMinor(annualMinor) {
    return Math.round(annualMinor / 12);
}

/**
 * Render a minor-unit amount. Decimals appear only when there is a remainder,
 * so $99 stays "$99" while $950.40 keeps its cents.
 */
export function formatAmountMinor(amountMinor, currency = "USD") {
    if (typeof amountMinor !== "number" || !Number.isFinite(amountMinor)) return null;
    const symbol = CURRENCY_SYMBOLS[currency] || `${currency} `;
    const units = amountMinor / 100;
    const body = Number.isInteger(units)
        ? units.toLocaleString("en-US")
        : units.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
          });
    return `${symbol}${body}`;
}

/**
 * Classify a cap from the contract.
 *
 * Returns one of:
 *   { kind: "number", value: n }  a real ceiling
 *   { kind: "unlimited" }         no ceiling
 *   { kind: "unconfigured" }      never set; the server denies additions
 */
export function readCap(value) {
    if (value === "unlimited") return { kind: "unlimited" };
    if (typeof value === "number" && Number.isFinite(value)) {
        return { kind: "number", value };
    }
    return { kind: "unconfigured" };
}

export function formatCount(count) {
    return Number(count).toLocaleString("en-US");
}

/**
 * A cap as a feature line, or null when it is unconfigured.
 * Never prints "Unlimited" for a null cap.
 */
export function formatCapFeature(value, singular, plural = `${singular}s`) {
    const cap = readCap(value);
    if (cap.kind === "unconfigured") return null;
    if (cap.kind === "unlimited") return `Unlimited ${plural}`;
    return `${formatCount(cap.value)} ${cap.value === 1 ? singular : plural}`;
}

/** The recruiterOffer block on a plans-API row, or null. */
export function offerOf(plan) {
    const offer = plan && plan.recruiterOffer;
    return offer && typeof offer === "object" ? offer : null;
}

/**
 * The public catalogue, in the order the API returned it.
 *
 * A row survives only when it carries a `public` recruiter offer whose code is
 * one we advertise. That drops legacy Starter/Growth/Scale rows, the hidden
 * Scale offer and any retired offer, even if the server ever stops filtering.
 */
export function selectPublicOffers(plans) {
    if (!Array.isArray(plans)) return [];
    return plans.filter((plan) => {
        const offer = offerOf(plan);
        if (!offer) return false;
        if (offer.visibility !== "public") return false;
        return PUBLIC_OFFER_CODES.includes(offer.offerCode);
    });
}

/** True for the Free offer, which has no checkout. */
export function isFreeOffer(offer) {
    return Boolean(offer) && offer.offerCode === "free";
}

/**
 * The interview allowance as display parts.
 * `total` is base + promotional and is always a MONTHLY number, on both cycles.
 */
export function interviewAllowance(offer) {
    const interviews = (offer && offer.interviews) || {};
    const base = readCap(interviews.basePerMonth);
    const total = readCap(interviews.totalPerMonth);
    const bonus =
        typeof interviews.bonusPerMonth === "number" ? interviews.bonusPerMonth : 0;
    const maxSeconds =
        typeof interviews.maxSecondsEach === "number"
            ? interviews.maxSecondsEach
            : MAX_INTERVIEW_SECONDS;
    return {
        base,
        bonus,
        total,
        maxSeconds,
        maxMinutes: Math.floor(maxSeconds / 60),
        rollover: Boolean(interviews.rollover),
    };
}

/**
 * Whether this offer may open checkout on this cycle, and why not when it may not.
 *
 * Every reason is a refusal to charge. The server owns the amount, so a
 * disagreement between its `amountMinor` and what this page advertises is
 * treated as the page being wrong and checkout stays shut.
 */
export function offerCheckoutState(plan, cycle) {
    const offer = offerOf(plan);
    if (!offer) return { ok: false, reason: "no_offer" };
    if (offer.visibility !== "public") return { ok: false, reason: "not_public" };
    if (!offer.purchasable) return { ok: false, reason: "not_purchasable" };
    if (isFreeOffer(offer)) return { ok: false, reason: "free_offer" };

    const price = offer.price;
    if (!price || typeof price !== "object") return { ok: false, reason: "no_price" };
    if (!price.checkoutEnabled) return { ok: false, reason: "checkout_disabled" };
    if (price.currency !== "USD") return { ok: false, reason: "currency_mismatch" };

    const months = CYCLE_MONTHS[cycle];
    if (price.intervalMonths !== months) return { ok: false, reason: "interval_mismatch" };

    const advertised = (ADVERTISED_AMOUNT_MINOR[offer.offerCode] || {})[months];
    if (typeof advertised !== "number") return { ok: false, reason: "offer_not_advertised" };
    if (price.amountMinor !== advertised) return { ok: false, reason: "amount_mismatch" };
    if (typeof price.priceId !== "number") return { ok: false, reason: "no_price_id" };

    return { ok: true, reason: null };
}

/**
 * The exact body `POST /create-order/` accepts.
 *
 * Offer code plus interval and nothing else. No uid (the server reads the auth
 * token), no currency, no amount, no priceId: the server resolves the price and
 * ignores or rejects anything a client asserts about money.
 */
export function checkoutRequestBody(offer, cycle) {
    const months = CYCLE_MONTHS[cycle];
    if (!offer || !offer.offerCode || typeof months !== "number") return null;
    return { offerCode: offer.offerCode, interval: String(months) };
}

/**
 * Validate a `POST /create-order/` reply before the payment sheet opens.
 *
 * Every field the client is about to act on has to be present and has to agree
 * with the offer the customer chose. A missing field is a mismatch, never a pass:
 * an absent `currency` previously slipped through and would have handed Razorpay
 * `undefined`, and an absent `offerCode` would let a reply for some other offer
 * be paid against this card.
 *
 * This runs before anything is captured, so a refusal here means nothing has
 * been charged.
 */
export function validateCheckoutOrder(order, offer, cycle) {
    if (!order || typeof order !== "object") return { ok: false, reason: "no_order" };
    const price = (offer && offer.price) || null;
    if (!price) return { ok: false, reason: "no_price" };

    if (typeof order.order_id !== "string" || order.order_id.trim() === "") {
        return { ok: false, reason: "no_order_id" };
    }
    if (typeof order.key_id !== "string" || order.key_id.trim() === "") {
        return { ok: false, reason: "no_key_id" };
    }
    if (!Number.isInteger(order.amount) || order.amount <= 0) {
        return { ok: false, reason: "bad_amount" };
    }
    if (order.amount !== price.amountMinor) return { ok: false, reason: "amount_mismatch" };
    if (typeof order.currency !== "string" || order.currency !== price.currency) {
        return { ok: false, reason: "currency_mismatch" };
    }

    // The terms the server says it recorded must be the terms we asked for.
    const expected = checkoutRequestBody(offer, cycle);
    if (!expected) return { ok: false, reason: "no_request" };
    if (order.offerCode !== expected.offerCode) return { ok: false, reason: "offer_mismatch" };
    if (String(order.interval) !== expected.interval) {
        return { ok: false, reason: "interval_mismatch" };
    }

    return { ok: true, reason: null };
}

/**
 * The price line for a card: the charged amount, plus the per-month equivalent
 * on the annual cycle.
 */
export function priceDisplay(offer, cycle) {
    const price = (offer && offer.price) || null;
    if (isFreeOffer(offer)) {
        return { amount: formatAmountMinor(0, "USD"), perLabel: null, equivalent: null };
    }
    if (!price || typeof price.amountMinor !== "number") return null;
    const months = CYCLE_MONTHS[cycle];
    const amount = formatAmountMinor(price.amountMinor, price.currency);
    if (months === 12) {
        const perMonth = formatAmountMinor(
            annualMonthlyEquivalentMinor(price.amountMinor),
            price.currency
        );
        return {
            amount,
            perLabel: "per year",
            equivalent: `${perMonth} a month, billed once a year`,
        };
    }
    return { amount, perLabel: "per month", equivalent: null };
}

// ──────────────────────────────────────────────────────────────────────────────
// Sidebar: interviews remaining, held, renewal
// ──────────────────────────────────────────────────────────────────────────────

/**
 * What the recruiter sidebar should show, read from the profile payload.
 *
 * `interviewQuota` is the server's authoritative counter set. Three outcomes:
 *
 *   { mode: "interviews" }  a real monthly allowance: remaining / held / renewal
 *   { mode: "unlimited" }   an explicitly unlimited allowance
 *   { mode: "legacy" }      the account predates the catalogue, or the key is
 *                           absent, so the existing minutes widget stays
 *
 * Legacy is the fallback for a MISSING key as well as for
 * `legacySecondsAccount: true`, so an older backend, a cached profile or a
 * candidate account all keep exactly the display they have today.
 */
export function sidebarQuota(profile) {
    const quota = profile && profile.interviewQuota;
    if (!quota || typeof quota !== "object") return { mode: "legacy" };
    if (quota.legacySecondsAccount) return { mode: "legacy" };
    if (quota.unlimited) {
        return {
            mode: "unlimited",
            planName: quota.planName || "",
            nextResetAt: quota.nextResetAt || null,
        };
    }

    const total = typeof quota.totalPerMonth === "number" ? quota.totalPerMonth : 0;
    const used = typeof quota.used === "number" ? quota.used : 0;
    const remaining =
        typeof quota.remaining === "number" ? quota.remaining : Math.max(0, total - used);
    const held = typeof quota.reserved === "number" ? quota.reserved : 0;
    const consumed = typeof quota.consumed === "number" ? quota.consumed : 0;

    return {
        mode: "interviews",
        planName: quota.planName || "",
        unconfigured: Boolean(quota.unconfigured),
        total,
        used,
        remaining,
        held,
        consumed,
        maxMinutes: Math.floor(
            (typeof quota.maxInterviewSeconds === "number"
                ? quota.maxInterviewSeconds
                : MAX_INTERVIEW_SECONDS) / 60
        ),
        nextResetAt: quota.nextResetAt || null,
        // Percentage of the allowance already spent, for the meter.
        usedPercent: total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0,
    };
}

/**
 * "1 October" style renewal date, or null when the server did not send one.
 *
 * Reads the calendar date out of the ISO string rather than through a `Date`.
 * `nextResetAt` is already expressed in the project timezone and carries its
 * offset ("2026-10-01T00:00:00+05:30"), so converting it to the browser's zone
 * would show a recruiter west of that offset the wrong renewal day: IST midnight
 * on 1 October is still 30 September in London. The day the server named is the
 * day the allowance renews, so that is the day we print.
 */
// `locale` comes from the caller (lib/i18n dateLocale("en-GB")): this file has
// plain node tests, so it must not import the i18n runtime.
export function formatResetDate(isoString, locale = "en-GB") {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(isoString || ""));
    if (!match) return null;
    const [, year, month, day] = match;
    const when = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    if (Number.isNaN(when.getTime())) return null;
    return when.toLocaleDateString(locale, {
        day: "numeric",
        month: "long",
        timeZone: "UTC",
    });
}

/**
 * Whether this account still allocates interview capacity to seats in MINUTES.
 *
 * True only for accounts the new catalogue does not cover: a
 * `legacySecondsAccount`, or one with no `interviewQuota` at all. Those really do
 * meter per-seat seconds (`SubUser.seconds_allocated`), so their team UI keeps
 * showing and editing that number, unchanged.
 *
 * False for every account on a recruiter offer. Their interview allowance is a
 * single monthly COUNT shared by the whole tenant, so a per-seat minute figure is
 * not merely redundant, it contradicts how the account is metered. For them the
 * field is hidden and a new seat is created without asking for one.
 */
export function usesLegacySeatMinutes(profile) {
    return sidebarQuota(profile).mode === "legacy";
}

// ──────────────────────────────────────────────────────────────────────────────
// Team management access
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Whether this account may open the tenant team-management area, and its seat
 * numbers.
 *
 * The Admin Panel is TENANT team management: it lists `subusers/<uid>` and
 * creates seats under the signed-in owner. It is not global staff admin, so the
 * question is "is this the tenant owner, and does the tenant have more than one
 * seat", never "is this plan expensive enough". The old `plan.id >= 4` gate is
 * gone: a plan id is not a privilege level, and the new Recruiter plan id is
 * >= 4, which would have shown the button to every paying customer.
 *
 * Owner versus seat is read from the SHAPE of the profile, which is decided by
 * which verified endpoint answered for the token's uid in
 * hooks/useFetchUserDetails.js:
 *
 *   `/profiles/<uid>/`  → a Profile. No `admin_uid`, no `admin`. The owner.
 *   `/subuser/<uid>/`   → a SubUser row, always carrying `admin_uid` and a
 *                         nested `admin` owner profile. A seat.
 *
 * That discriminator is not client-writable: a seat cannot make itself an owner
 * by editing a field, because the field it would have to remove is the one the
 * server attached. `Profile.role` is deliberately NOT used, since it is
 * self-settable through the profile serializer.
 *
 * Seat entitlement comes from the server's `interviewQuota.seats`. When that key
 * is absent the account is treated as a legacy tenant owner and keeps access, so
 * this change never locks an existing customer out of their own team.
 */
export function teamAccess(profile) {
    if (!profile || typeof profile !== "object") {
        return { canManageTeam: false, reason: "no_profile" };
    }
    // A seat, not the tenant owner.
    if (profile.admin_uid !== undefined || profile.admin !== undefined) {
        return { canManageTeam: false, reason: "seat" };
    }

    const quota = profile.interviewQuota;
    if (!quota || typeof quota !== "object") {
        // No server quota block yet (older backend, candidate profile, or a
        // cached payload). Only an account that looks like a tenant owner with a
        // plan keeps access, and it is keyed on HAVING a plan, not on its id.
        if (profile.plan && typeof profile.plan === "object") {
            return { canManageTeam: true, reason: "legacy_owner", seats: null };
        }
        return { canManageTeam: false, reason: "no_quota" };
    }

    if (quota.legacySecondsAccount) {
        return { canManageTeam: true, reason: "legacy_owner", seats: quota.seats || null };
    }

    const seats = quota.seats || {};
    const total = readCap(seats.total);
    const used = typeof seats.used === "number" ? seats.used : null;
    if (total.kind === "unlimited") {
        return { canManageTeam: true, reason: "seats", seats: { used, total } };
    }
    if (total.kind === "number" && total.value > 1) {
        return { canManageTeam: true, reason: "seats", seats: { used, total } };
    }
    // The cap allows no team, but the tenant may already HAVE one: a downgrade
    // to Free, or a cap that was lowered, leaves real seats in place. Those
    // accounts must keep reaching the team page so they can see and remove the
    // seats they have. `used` counts the owner, so more than one means real
    // seats exist. Adding is blocked separately by `seatSummary().atCap`, which
    // is what the Add button reads: a cap of 1 or an unconfigured cap must
    // disable ADDING, not hide all management.
    if (used !== null && used > 1) {
        return { canManageTeam: true, reason: "existing_seats", seats: { used, total } };
    }
    // Owner only, with no room for more: there is genuinely no team to manage.
    return { canManageTeam: false, reason: "single_seat", seats: { used, total } };
}

/**
 * Seat counts for the team page header, including the owner.
 * Returns null when the server has not sent a seat block.
 *
 * `atCap` answers only one question: may another seat be ADDED? It is never used
 * to decide whether existing seats can be seen or removed, so an account that is
 * already over its cap keeps full management of what it has while the Add button
 * is disabled. An unconfigured cap counts as at-cap, because the server denies
 * additions against it.
 */
export function seatSummary(profile) {
    const quota = profile && profile.interviewQuota;
    const seats = quota && quota.seats;
    if (!seats || typeof seats !== "object") return null;
    const total = readCap(seats.total);
    const used = typeof seats.used === "number" ? seats.used : null;

    if (total.kind === "unconfigured") {
        return {
            used,
            total,
            label:
                used === null
                    ? null
                    : `${used} ${used === 1 ? "seat" : "seats"} in use. This plan has no seat limit set.`,
            atCap: true,
            overCap: false,
        };
    }
    if (used === null) {
        return { used, total, label: null, atCap: false, overCap: false };
    }
    if (total.kind === "unlimited") {
        return {
            used,
            total,
            label: `${used} of unlimited seats in use`,
            atCap: false,
            overCap: false,
        };
    }
    return {
        used,
        total,
        label: `${used} of ${total.value} seats in use, including you`,
        atCap: used >= total.value,
        overCap: used > total.value,
    };
}
